import { QueryClient } from '@tanstack/react-query';

// React Query client configuration
// Optimized for enterprise task management with stale-while-revalidate strategy
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Refetch on window focus for real-time collaboration
      refetchOnWindowFocus: true,
      // Keep data fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache data for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests 2 times
      retry: 2,
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

// Query keys for consistent cache management
export const QUERY_KEYS = {
  // Tasks
  tasks: ['tasks'] as const,
  task: (id: string) => ['tasks', id] as const,
  myTasks: ['tasks', 'my-tasks'] as const,
  todaysTasks: ['tasks', 'today'] as const,
  overdueTasks: ['tasks', 'overdue'] as const,
  upcomingTasks: ['tasks', 'upcoming'] as const,
  projectTasks: (projectId: string) => ['tasks', 'project', projectId] as const,

  // Delegated Tasks
  delegatedTasks: ['delegated-tasks'] as const,
  delegatedTask: (id: string) => ['delegated-tasks', id] as const,
  delegatedByMe: (userId: string) => ['delegated-tasks', 'delegated', userId] as const,
  assignedToMe: (userId: string) => ['delegated-tasks', 'assigned', userId] as const,

  // Projects
  projects: ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
  projectHealth: ['projects', 'health'] as const,
  projectStats: (id: string) => ['projects', id, 'stats'] as const,

  // Time
  timeLogs: ['time-logs'] as const,
  taskTimeLogs: (taskId: string) => ['time-logs', 'task', taskId] as const,
  dailySummary: (date?: string) => ['time-logs', 'daily', date] as const,
  weeklySummary: (weekStart?: string) => ['time-logs', 'weekly', weekStart] as const,

  // Performance
  dailyPerformance: (date?: string) => ['performance', 'daily', date] as const,
  weeklyPerformance: (weekStart?: string) => ['performance', 'weekly', weekStart] as const,

  // Bandwidth
  myBandwidth: (date?: string) => ['bandwidth', 'me', date] as const,
  userBandwidth: (userId: string, date?: string) => ['bandwidth', 'user', userId, date] as const,
  teamBandwidth: (teamId: string, date?: string) => ['bandwidth', 'team', teamId, date] as const,

  // Team Workload
  TEAM_WORKLOAD: ['team', 'workload'] as const,
  teamWorkload: (teamId?: string) => ['team', 'workload', teamId] as const,

  // Attendance
  myAttendance: ['attendance', 'me'] as const,
  userAttendance: (userId: string) => ['attendance', 'user', userId] as const,
  teamAttendance: (teamId: string) => ['attendance', 'team', teamId] as const,

  // Leave
  myLeaves: ['leaves', 'me'] as const,
  leaveBalance: ['leaves', 'balance'] as const,
  pendingApprovals: ['leaves', 'pending-approvals'] as const,
  teamCalendar: (teamId: string) => ['leaves', 'calendar', teamId] as const,

  // Reviews
  reviews: ['reviews'] as const,
  review: (id: string) => ['reviews', id] as const,
  myPendingReviews: ['reviews', 'pending', 'me'] as const,
  PENDING_REVIEWS: ['reviews', 'pending'] as const,
  teamPendingReviews: ['reviews', 'pending', 'team'] as const,
  taskReview: (taskId: string) => ['reviews', 'task', taskId] as const,

  // Dashboard
  individualDashboard: ['dashboard', 'individual'] as const,
  managerDashboard: ['dashboard', 'manager'] as const,
  projectManagerDashboard: ['dashboard', 'project-manager'] as const,
  adminDashboard: ['dashboard', 'admin'] as const,

  // Users & Teams
  users: ['users'] as const,
  user: (id: string) => ['users', id] as const,
  teams: ['teams'] as const,
  team: (id: string) => ['teams', id] as const,
  teamMembers: (teamId: string) => ['teams', teamId, 'members'] as const,

  // Notifications
  notifications: ['notifications'] as const,
  unreadNotifications: ['notifications', 'unread'] as const,

  // Calendar
  calendarEvents: ['calendar', 'events'] as const,
  calendarEventsRange: (startDate: string, endDate: string) =>
    ['calendar', 'events', startDate, endDate] as const,

  // Team Management
  teamMembers: (teamId?: string) => ['team', 'members', teamId] as const,
  teamMemberDetail: (userId: string) => ['team', 'member', userId] as const,

  // KRA/KPI
  kras: (filters?: any) => ['kras', filters] as const,
  kra: (id: string) => ['kras', id] as const,
  userKRAs: (userId: string) => ['kras', 'user', userId] as const,
  kpis: (kraId?: string) => ['kpis', kraId] as const,
  kpi: (id: string) => ['kpis', id] as const,
  userKPIs: (userId: string) => ['kpis', 'user', userId] as const,
  kpiHistory: (kpiId: string) => ['kpis', 'history', kpiId] as const,

  // Performance
  teamPerformance: ['performance', 'team'] as const,
  userPerformance: (userId: string, period?: string) => ['performance', 'user', userId, period] as const,
  performanceReviews: (userId?: string, status?: string) => ['performance', 'reviews', userId, status] as const,
  performanceReview: (id: string) => ['performance', 'reviews', id] as const,

  // Departments
  departments: ['departments'] as const,
  department: (id: string) => ['departments', id] as const,
  departmentPerformance: (departmentId: string, period?: string) => ['departments', departmentId, 'performance', period] as const,
  departmentsComparison: (period?: string) => ['departments', 'comparison', period] as const,
  departmentTaskDistribution: (departmentId: string) => ['departments', departmentId, 'tasks', 'distribution'] as const,
  departmentMemberWorkload: (departmentId: string) => ['departments', departmentId, 'workload'] as const,
  departmentTeams: (departmentId: string) => ['departments', departmentId, 'teams'] as const,
};

export default queryClient;
